import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import validate from 'validate.js';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { SelectField } from 'components';
import { Typography, TextField, Card, CardHeader, CardContent, Stack, Grid, Divider, Alert } from '@mui/material';
import _ from 'underscore';

import DateTimePicker from 'components/DatePicker/DateTimePicker';
import { SetFormState, GetDropdowns,
	GetIncident } from '../../actions/PagerDutyIncidentsActions';

const schema = {
	internalId: {
		presence: false,
	},
	status: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	priority: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	urgency: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	createdOn: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	stakeholder: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	resolvedOn: {
		presence: false,
	},
	secondsToFirstAck: {
		presence: false,
	},
	secondsToResolve: {
		presence: false,
	},
	escalationPolicyId: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	serviceId: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	alarmRef: {
		presence: false,
	},
	severity: {
		presence: false,
	},
	description: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const useStyles = makeStyles((theme) => ({
	root: {},
	fields: {
		margin: theme.spacing(-1),
		display: 'flex',
		flexWrap: 'wrap',
		'& > *': {
			flexGrow: 1,
			margin: theme.spacing(1),
		},
	},
	submitButton: {
		marginTop: theme.spacing(2),
		width: '100%',
	},
	divider: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
	noBorder: {
		border: 'none',
	}
}));

const PagerDutyIncidentDialogForm = (props) => {
	const { incidentId, allDisabled = false, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.pagerDutyIncidentsReducer.formState);
	const dropdowns = useSelector((state) => state.pagerDutyIncidentsReducer.dropdowns);
	const isIncidentClosed = useSelector((state) => state.pagerDutyIncidentsReducer.isIncidentClosed);

	const _hasError = field => (incidentId !== 0 && formState.errors[field]) || !!(formState.touched[field] && formState.errors[field]);
	const _handleChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	useEffect(() => {
		dispatch(GetDropdowns());
		if (incidentId !== 0) {
			dispatch(GetIncident(incidentId));
		} else {
			setFormState({
				...formState,
				values: {
					...formState.values,
					secondsToFirstAck: 120,
					createdOn: Date.now(),
					stakeholder: 'DAS NOC',
					secondsToResolve: null,
					status: 'Open',
				},
				touched: {
					...formState.touched,
					secondsToFirstAck: true,
					secondsToResolve: true,
					createdOn: true
				}
			});
		}
	}, []);

	useMemo(() => {
		const newTouched = {};

		if (formState.submit) {
			_.each(_.keys(schema), item => {
				newTouched[item] = true;
			});
		}
		const errors = validate(formState.values, schema, {
			fullMessages: false
		});

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
			touched: {
				...formState.touched,
				...newTouched
			}
		});
	}, [formState.values, formState.submit]);

	return (
		<div className={classes.fields}>
			<Grid container spacing={2}>
				{isIncidentClosed && (
					<Grid item md={12} xs={12}>
						<Alert severity="info">This incident is closed.</Alert>
					</Grid>
				)}
				<Grid item md={9} xs={9}>
					<Grid container spacing={2}>
						<Grid item md={12} xs={12}>
							<Divider><Typography variant="h5" gutterBottom component="div">Incident Details</Typography></Divider>
						</Grid>
						{/* New row */}
						<Grid item md={6} xs={6}>
							<TextField
								id="internalId"
								name="internalId"
								label="InternalId"
								variant="outlined"
								fullWidth
								disabled
								value={formState.values.internalId || ''}
								InputLabelProps={{
									shrink: true,
								}}
								onChange={_handleChange}
							/>
						</Grid>
						<Grid item md={6} xs={6}>
							<SelectField
								id="status"
								name="status"
								value={formState.values.status || ''}
								onChange={_handleChange}
								hintText="Status"
								disabled={allDisabled || isIncidentClosed || incidentId === 0}
								menuItems={dropdowns.status || []}
								error={
									_hasError('status')
										? formState.errors.status[0]
										: null
								}
							/>
						</Grid>
						<Grid item md={4} xs={4}>
							<SelectField
								id="priority"
								name="priority"
								hintText="Priority"
								error={
									_hasError('priority')
										? formState.errors.priority[0]
										: null
								}
								disabled={allDisabled}
								menuItems={dropdowns.priority || []}
								value={formState.values.priority || ''}
								onChange={_handleChange}
							/>
						</Grid>
						<Grid item md={4} xs={4}>
							<SelectField
								id="urgency"
								name="urgency"
								hintText="Urgency"
								error={
									_hasError('urgency')
										? formState.errors.urgency[0]
										: null
								}
								disabled={allDisabled}
								menuItems={dropdowns.urgency || []}
								value={formState.values.urgency || ''}
								onChange={_handleChange}
							/>
						</Grid>
						<Grid item md={4} xs={4}>
							<SelectField
								id="stakeholder"
								name="stakeholder"
								hintText="Stakeholder"
								error={
									_hasError('stakeholder')
										? formState.errors.stakeholder[0]
										: null
								}
								disabled={allDisabled}
								menuItems={dropdowns.stakeholder || []}
								value={formState.values.stakeholder || ''}
								onChange={_handleChange}
							/>
						</Grid>
						<Grid item md={6} xs={6}>
							<SelectField
								id="escalationPolicyId"
								name="escalationPolicyId"
								hintText="Escalation policy"
								disabled={allDisabled}
								error={
									_hasError('escalationPolicyId')
										? formState.errors.escalationPolicyId[0]
										: null
								}
								menuItems={dropdowns.escalationPolicy || []}
								value={formState.values.escalationPolicyId || ''}
								onChange={_handleChange}
							/>
						</Grid>
						<Grid item md={6} xs={6}>
							<SelectField
								id="serviceId"
								name="serviceId"
								hintText="Service"
								disabled={allDisabled}
								error={
									_hasError('serviceId')
										? formState.errors.serviceId[0]
										: null
								}
								menuItems={dropdowns.service || []}
								value={formState.values.serviceId || ''}
								onChange={_handleChange}
							/>
						</Grid>
						<Grid item md={6} xs={6}>
							<SelectField
								id="alarmRef"
								name="alarmRef"
								hintText="Alarm Ref."
								disabled={allDisabled}
								error={
									_hasError('alarmRef')
										? formState.errors.alarmRef[0]
										: null
								}
								menuItems={dropdowns.alarmRef || []}
								value={formState.values.alarmRef || ''}
								onChange={_handleChange}
							/>
						</Grid>
						<Grid item md={6} xs={6}>
							<SelectField
								id="severity"
								name="severity"
								hintText="Severity"
								disabled={allDisabled}
								error={
									_hasError('severity')
										? formState.errors.severity[0]
										: null
								}
								menuItems={dropdowns.severity || []}
								value={formState.values.severity || ''}
								onChange={_handleChange}
							/>
						</Grid>
						{/* New row */}
						<Grid item md={12} xs={12}>
							<TextField
								id="description"
								name="description"
								label="Description"
								disabled={allDisabled}
								error={
									_hasError('description')
										? formState.errors.description[0]
										: null
								}
								variant="outlined"
								multiline
								rows={2}
								fullWidth
								value={formState.values.description || ''}
								InputLabelProps={{
									shrink: true,
								}}
								onChange={_handleChange}
							/>
						</Grid>
					</Grid>
				</Grid>
				<Grid item md={3} xs={3}>
					<Card>
						<CardHeader
							title="Additional info"
							titleTypographyProps={{ align: 'center' }}
						/>
						<CardContent>
							<Stack spacing={2}>
								<DateTimePicker
									id="createdOn"
									name="createdOn"
									label="Created on"
									error={
										_hasError('createdOn')
											? formState.errors.createdOn[0]
											: null
									}
									variant="filled"
									value={formState.values.createdOn || Date.now()}
									minutesStep={1}
									onChange={(date) => _handleChange({ target: { name: 'createdOn', type: 'string', value: date.toISOString() } })}
								/>
								<DateTimePicker
									id="resolvedOn"
									name="resolvedOn"
									label="Resolved on"
									error={
										_hasError('resolvedOn')
											? formState.errors.resolvedOn[0]
											: null
									}
									variant="filled"
									value={formState.values.resolvedOn || ''}
									disabled={incidentId === 0 || formState.values.status === 'Open'}
									minutesStep={1}
									onChange={(date) => _handleChange({ target: { name: 'resolvedOn', type: 'string', value: date.toISOString() } })}
								/>
								<TextField
									id="secondsToFirstAck"
									type="number"
									fullWidth
									name="secondsToFirstAck"
									label="Seconds to 1st ACK"
									error={
										_hasError('secondsToFirstAck')
											? formState.errors.secondsToFirstAck[0]
											: null
									}
									variant="filled"
									disabled
									defaultValue={120}
									value={formState.values.secondsToFirstAck || ''}
									onChange={_handleChange}
								/>
								<TextField
									id="secondsToResolve"
									type="number"
									fullWidth
									name="secondsToResolve"
									label="Seconds to resolve"
									error={
										_hasError('secondsToResolve')
											? formState.errors.secondsToResolve[0]
											: null
									}
									variant="filled"
									disabled
									value={formState.values.secondsToResolve || ''}
									onChange={_handleChange}
								/>
							</Stack>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</div>
	);
};

PagerDutyIncidentDialogForm.propTypes = {
	incidentId: PropTypes.number,
	allDisabled: PropTypes.bool
};

export default PagerDutyIncidentDialogForm;
